import { useNavigate } from 'react-router-dom'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { LogErrors } from './../../../../../../utilities/LogErrors'

import { InstructorBase } from './../../../../../Layouts/Instructor/V2/InstructorBase'

import { CreateAgenda as BLCreateAgenda, GetCourses as BLGetCourses, GetEvaluations as BLGetEvaluations, ValidateAgendasBatch as BLValidateAgendasBatch, GetExistingAgenda as BLGetExistingAgenda } from './../../../../../../utilities/BL/Instructor/Agendas'
import { Search } from './../../../../../Common/Tables/Search'
import { GetAllDataGroups } from './../../../../../../utilities/BL/Instructor/Groups'
import { ValidateSession } from './../../../../../../utilities/BL/Sessions'
import { useFloatMessage, MESSAGE_TYPE } from './../../../../../Providers/FloatMessage'
import { Details } from  './../../../../../Common/Modals/Details'
import { WEBPAGES } from './../../../../../../utilities/EL/WebPages'

import { Loading } from './../../../../../Common/Loading'


/**
 * Componente: Create.js
 * Descripción: Permite agendar evaluaciones a grupos de estudiantes.
 * * CAMBIOS RECIENTES (Estrategia Opción B):
 * 1. Se eliminó la lógica de "Auto-copiado al hacer check" para evitar confusiones.
 * 2. Se implementaron "Valores por Defecto" (Hoy + 30 días) al activar una evaluación nueva.
 * 3. Se agregó la función `applyValuesToActiveEvaluations` y un enlace en la UI para replicar
 * la configuración de una evaluación a las demás activas, respetando los límites (clamping) de oportunidades.
 */

const COPY_OPPORTUNITIES = true

const Create = () => {
  const navigate = useNavigate()
  const { showMessage } = useFloatMessage()

  // ==========================================
  // ESTADO GENERAL DE LA UI
  // ==========================================
  const [errorMessage, setErrorMessage] = useState(null)
  const [isWorking, setIsWorking] = useState(false)
  const [step, setStep] = useState(1) // 1= Cursos, 2= Grupos, 3= Evaluaciones
  const [inputErrors, setInputErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)

  // ==========================================
  // HELPERS DE UTILIDAD
  // ==========================================
  
  /**
   * Limpia el error de un campo específico en el estado de errores.
   */
  const clearFieldError = (id, field) => {
    setInputErrors(prev => {
      if (!prev[id]) return prev
      const copy = { ...prev }
      const { [field]: _omit, ...rest } = copy[id]
      copy[id] = rest
      if (Object.keys(copy[id]).length === 0) delete copy[id]
      return copy
    })
  }

  /**
   * Genera los valores por defecto para una nueva evaluación.
   * CORRECCIÓN: Usa hora local en lugar de UTC para evitar el desfase de día.
   */
  const getSmartDefaults = () => {
    const now = new Date()
    const future = new Date()
    future.setDate(future.getDate() + 30)

    // Función auxiliar para formatear a YYYY-MM-DD respetando la zona horaria LOCAL
    const formatDateLocal = (date) => {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0') // Meses son 0-11
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }

    return {
      startDate: formatDateLocal(now),
      startTime: '00:00', // Sugerencia: Podrías poner '12:00' si prefieres mediodía
      finishDate: formatDateLocal(future),
      finishTime: '23:59',
      opportunities: 1
    }
  }

  /**
   * Ajusta un valor numérico dentro de un rango (min-max).
   * Se usa para asegurar que al copiar oportunidades no excedamos el límite permitido.
   */
  const clamp = (val, min, max) => {
    if (typeof val !== 'number' || Number.isNaN(val)) return undefined
    if (typeof max === 'number') return Math.max(min, Math.min(val, max))
    return Math.max(min, val)
  }

  const toHHMMSS = (t) => (t ? (t.length === 5 ? `${t}:00` : t) : '')
  const normalizeCourses = (arr) => (arr ?? []).map(c => ({ id: c.idCourse, name: c.nameCourse, level: c.level }))

  // ==========================================
  // MODALES Y DATOS MASIVOS
  // ==========================================
  const [showMassiveSummaryModal, setShowMassiveSummaryModal] = useState(false)
  const [massiveSummary, setMassiveSummary] = useState(null)

  // ==========================================
  // GESTIÓN DE GRUPOS
  // ==========================================
  const [allRows, setAllRows] = useState([])
  const [searchText, setSearchText] = useState('')
  const allRowsRef = useRef([])
  const [selectedGroupId, setSelectedGroupId] = useState(null)
  
  useEffect(() => { allRowsRef.current = allRows }, [allRows])

  const filteredRows = useMemo(() => {
    if (!searchText) return allRows
    return allRows.filter((row) => (row.groupName ?? '').toLowerCase().includes(searchText.toLowerCase()))
  }, [searchText, allRows])

  const handleSearch = (value) => setSearchText(value)

  // ==========================================
  // GESTIÓN DE CURSOS
  // ==========================================
  const [courses, setCourses] = useState(null)
  const [selectedCourseId, setSelectedCourseId] = useState(null)

  // ==========================================
  // GESTIÓN DE EVALUACIONES
  // ==========================================
  const [evaluations, setEvaluations] = useState(null)
  const [selectedIds, setSelectedIds] = useState([])
  const isSelected = (id) => selectedIds.includes(id)

  // Datos controlados por evaluación (nombre, fechas, horas, oportunidades)
  // Estructura: { [idEvaluacion]: { startDate, finishDate, ... } }
  const [evaluationsData, setEvaluationsData] = useState({})

  /**
   * Normaliza los datos provenientes de la BD (si la evaluación ya estaba agendada).
   */
  const normalizeExistingData = (raw, evMaxOpps) => {
    const toTimeHHmm = (s) => {
      if (!s) return ''
      const parts = String(s).split(':')
      if (parts.length >= 2) return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`
      return s
    }

    const n = (k, fallback) => {
      const v = Number(raw?.[k])
      return Number.isFinite(v) ? v : fallback
    }

    const s = (k, fallback = '') => {
      const v = raw?.[k]
      return (v ?? fallback)
    }

    const startDate = s('startDate', s('fechaInicio', s('dateStart', '')))
    const finishDate = s('finishDate', s('fechaFin', s('dateFinish', '')))
    const startTime = toTimeHHmm(s('startHour', s('horaInicio', s('timeStart', ''))))
    const finishTime = toTimeHHmm(s('finishHour', s('horaFin', s('timeFinish', ''))))
    const name = s('name', s('customName', s('titulo', '')))

    let opportunities = n('opportunities', n('oportunities', n('intentos', undefined)))
    if (typeof opportunities !== 'undefined') {
      opportunities = clamp(opportunities, 1, evMaxOpps)
    }

    return {
      startDate, finishDate, startTime, finishTime, opportunities, name,
      isExist: !!raw?.isExist,
      idGroupCourse: raw?.idGroupCourse ?? raw?.IdGroupCourse ?? null,
      IdAgenda: raw?.IdAgenda ?? null
    }
  }

  /**
   * Actualiza el estado local de una evaluación específica.
   */
  const updateEvalData = (id, patch) => {
    setEvaluationsData(prev => {
      const merged = { ...prev[id], ...patch }
      return { ...prev, [id]: merged }
    })
  }

  /**
   * FUNCIÓN CLAVE (Opción B): Aplica los valores de una evaluación origen
   * a TODAS las demás evaluaciones que estén seleccionadas (checked) en ese momento.
   * Resuelve el "punto ciego" usando clamp() para las oportunidades.
   */
  const applyValuesToActiveEvaluations = useCallback((sourceId) => {
    try {
      // 1. Obtener datos de la fuente
      const sourceData = evaluationsData[sourceId]
      if (!sourceData) return

      // Extraemos solo lo copiable (fechas, horas y oportunidades)
      // No copiamos IDs ni metadatos de existencia
      const { startDate, finishDate, startTime, finishTime, opportunities } = sourceData

      // 2. Iterar sobre todos los IDs seleccionados
      setEvaluationsData(prev => {
        const nextState = { ...prev }
        
        selectedIds.forEach(targetId => {
          // No nos copiamos a nosotros mismos
          if (targetId === sourceId) return

          // Obtener configuración de la evaluación destino (para saber su max oportunidades)
          const targetConfig = evaluations?.find(e => e.id === targetId)
          const maxOppsForTarget = targetConfig?.opportunities ?? 1

          // 3. Preparar el patch
          const patch = {
            startDate,
            finishDate,
            startTime,
            finishTime
          }

          // 4. Copiar oportunidades inteligentemente (Clamping)
          if (COPY_OPPORTUNITIES && typeof opportunities !== 'undefined') {
            patch.opportunities = clamp(Number(opportunities), 1, maxOppsForTarget)
          }

          // Fusionar con lo que ya tenía el destino
          nextState[targetId] = { ...(nextState[targetId] || {}), ...patch }
        })

        return nextState
      })

      showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Datos aplicados a las evaluaciones seleccionadas.' })

    } catch (error) {
      console.error('Error en applyValuesToActiveEvaluations', error)
      showMessage({ type: MESSAGE_TYPE.BAD, text: 'Ocurrió un error al copiar los datos.' })
    }
  }, [evaluationsData, selectedIds, evaluations, showMessage]) // Dependencias

  // ==========================================
  // RESET Y CARGAS
  // ==========================================
  const resetAll = () => {
    try {
      setErrorMessage(null)
      setIsWorking(false)
      setSelectedGroupId(null)
      setSelectedCourseId(null)
      setCourses(null)
      setEvaluations(null)
      setSelectedIds([])
      setEvaluationsData({})
      setSearchText('')
      setStep(1)
    } catch (error) {}
  }

  const resetAfterCreateKeepCourses = () => {
    try {
      setErrorMessage(null)
      setIsWorking(false)
      // Mantener cursos cargados
      setSelectedCourseId(null)
      setSelectedGroupId(null)
      setAllRows([])
      setEvaluations(null)
      setSelectedIds([])
      setEvaluationsData({})
      setSearchText('')
      setStep(1)
    } catch (error) {}
  }

  const NormalizeGroups = (resp) => {
    if (!resp?.success) return []
    return [
      ...(resp?.data?.visibles ?? []).map(x => ({ ...x, visible: true })),
      ...(resp?.data?.noVisibles ?? []).map(x => ({ ...x, visible: false })),
    ]
  }

  const LoadPage = async () => {
    try {
      const resp = await BLGetCourses()
      if (resp?.success) {
        setCourses(normalizeCourses(resp.data))
        setStep(1)
      } else {
        setErrorMessage(resp?.message ?? 'No fue posible cargar cursos.')
      }
    } catch (error) {
      await LogErrors('LoadPage - Create', error)
      setErrorMessage('Ocurrió un error al cargar cursos.')
    }
  }

  useEffect(() => { LoadPage() }, [])

  useEffect(() => {
    async function Validate() {
      const tmpSession = await ValidateSession()
      if (tmpSession.success) {
        if (!tmpSession.data.user.userStatus === 2) {
          navigate('/confirm')
        }
      } else {
        navigate(`/?logout=${process.env.REACT_APP_LOGOUT_PARAMETER}`)
      }
    }
    Validate()
  }, [navigate])

  // ==========================================
  // MANEJADORES DE SELECCIÓN (Pasos 1 y 2)
  // ==========================================
  const handleSelectGroup = async (group) => {
    try {
      setIsWorking(true)
      setErrorMessage(null)
      setSelectedGroupId(group.idGroup)
      setEvaluations(null)
      setSelectedIds([])
      setEvaluationsData({})

      const resp = await BLGetEvaluations(selectedCourseId)
      if (resp?.success) {
        setEvaluations(resp.data ?? [])
        setStep(3)
      } else {
        setErrorMessage(resp?.message ?? 'No fue posible cargar evaluaciones.')
      }
    } catch (e) {
      await LogErrors('handleSelectGroup - Create', e)
      setErrorMessage('Ocurrió un error al cargar evaluaciones.')
    } finally {
      setIsWorking(false)
    }
  }

  const handleSelectCourse = async (courseId) => {
    try {
      setIsWorking(true)
      setErrorMessage(null)
      setSelectedCourseId(courseId)
      setSelectedGroupId(null)
      setEvaluations(null)
      setSelectedIds([])
      setEvaluationsData({})

      if (!courseId) return

      const resp = await GetAllDataGroups()
      if (resp?.success) {
        setAllRows(NormalizeGroups(resp))
        setStep(2)
      } else {
        setErrorMessage(resp?.message ?? 'No fue posible cargar grupos.')
      }
    } catch (e) {
      await LogErrors('handleSelectCourse - Create', e)
      setErrorMessage('Ocurrió un error al cargar grupos.')
    } finally {
      setIsWorking(false)
    }
  }

  // ==========================================
  // LÓGICA DE SELECCIÓN DE EVALUACIONES (Paso 3)
  // ==========================================
  
  // Consulta datos existentes en BD (si es reagendar)
  const tryPrefillFromExisting = async (ev) => {
    try {
      if (!selectedCourseId || !selectedGroupId) return null
      
      const resp = await BLGetExistingAgenda(selectedCourseId, selectedGroupId, ev.id)
      
      if (!resp?.success || !resp?.data) return null

      // Normalizamos y hacemos clamp de oportunidades según BD
      const normalized = normalizeExistingData(resp.data, ev.opportunities)
      return {
        ...normalized,
        idItem: resp.data.idItem,
        idGroupCourse: resp.data.idGroupCourse,
        IdAgenda: resp.data.id
      }
    } catch (e) {
      await LogErrors('tryPrefillFromExisting - Create', e)
      return null
    }
  }

  /**
   * Maneja el Check/Uncheck de una evaluación individual.
   * CAMBIO: Ya no copia del anterior. Ahora, si es nuevo, pone Defaults (Hoy + 30).
   */
  const toggleSelect = async (id) => {
    try {
      setIsLoading(true)
      const ev = (evaluations || []).find(x => x.id === id)
      if (!ev) return

      const isAdding = !selectedIds.includes(id)
      
      // CASO A: Deseleccionar (Quitar)
      if (!isAdding) {
        const updated = selectedIds.filter(i => i !== id)
        setSelectedIds(updated)
        // Limpiamos errores visuales de este ID si existían
        setInputErrors(prev => {
            const copy = {...prev}
            delete copy[id]
            return copy
        })
        return
      }

      // CASO B: Seleccionar (Agregar)
      
      // 1. Intentar buscar datos previos en BD (Reagendado)
      const existing = await tryPrefillFromExisting(ev)
      if (existing) {
        // Preparar patch desde BD
        const patch = { ...existing }
        if (!COPY_OPPORTUNITIES) delete patch.opportunities

        setEvaluationsData(prev => ({
          ...prev,
          [id]: {
            ...(prev[id] || {}),
            ...patch,
            __existing: true, // Bandera interna
            idItem: existing.idItem ?? (prev[id]?.idItem),
            idGroupCourse: existing.idGroupCourse,
            IdAgenda: existing.IdAgenda
          }
        }))
        setSelectedIds(prev => [...prev, id])
        return
      }

      // 2. Si es una NUEVA agenda: Establecer VALORES PREDETERMINADOS (Smart Defaults)
      const defaults = getSmartDefaults()
      
      // Validar que el default de oportunidades no exceda el máximo de ESTA evaluación
      defaults.opportunities = clamp(defaults.opportunities, 1, ev.opportunities)

      setEvaluationsData(prev => ({ 
          ...prev, 
          [id]: { 
              ...(prev[id] || {}), 
              ...defaults 
          } 
      }))
      setSelectedIds(prev => [...prev, id])

    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Seleccionar/Deseleccionar TODO.
   * CAMBIO: Si selecciona todo y no hay datos en BD, aplica Defaults a todos.
   */
  const toggleSelectAll = async () => {
    try {
      setIsLoading(true)
      if (!evaluations?.length) return
      const allIds = evaluations.map(r => r.id)
      const selectingAll = selectedIds.length !== evaluations.length

      if (!selectingAll) {
        setSelectedIds([]) // Deseleccionar todo
        return
      }

      // ===== Seleccionar todo =====
      setIsWorking(true)
      setErrorMessage(null)

      // 1. Obtener datos existentes en paralelo (Bulk logic simulada)
      const existingById = {}
      const tasks = evaluations.map(async (ev) => {
        const existing = await tryPrefillFromExisting(ev)
        if (existing) existingById[ev.id] = existing
      })
      await Promise.all(tasks)

      // 2. Construir estado
      setEvaluationsData(prev => {
        const next = { ...prev }

        for (const id of allIds) {
          const ev = evaluations.find(e => e.id === id)
          const base = next[id] || {}

          // Prioridad 1: Datos Existentes en BD
          if (existingById[id]) {
            const patch = { ...existingById[id] }
            if (!COPY_OPPORTUNITIES) delete patch.opportunities

            next[id] = {
              ...base,
              ...patch,
              __existing: true,
              idItem: patch.idItem ?? base?.idItem,
              idGroupCourse: patch.idGroupCourse,
              IdAgenda: patch.IdAgenda
            }
            continue
          }

          // Prioridad 2: Valores Predeterminados (Hoy + 30)
          const defaults = getSmartDefaults()
          defaults.opportunities = clamp(defaults.opportunities, 1, ev?.opportunities)
          
          // Solo sobreescribimos si no tenía datos o si queremos forzar el default al abrir
          // Aquí fusionamos base + defaults
          next[id] = { ...base, ...defaults }
        }
        return next
      })

      setSelectedIds(allIds)

    } catch (error) {
      await LogErrors('toggleSelectAll - Create', error)
    } finally {
      setIsWorking(false)
      setIsLoading(false)
    }
  }

  const handleChangeOportunity = useCallback((id, value) => {
    try {
      const n = parseInt(value, 10)
      const val = Number.isInteger(n) && n > 0 ? n : 1
      updateEvalData(id, { opportunities: val })
    } catch (error) {}
  }, [])

  // Resolución final de oportunidades antes de enviar
  const ResolveOportunities = (d, ev) => {
    try {
      const fromUI = Number(d?.opportunities)
      if (Number.isInteger(fromUI) && fromUI > 0) {
        return clamp(fromUI, 1, ev?.opportunities)
      }
      const fromEval = Number(ev?.opportunities)
      if (Number.isInteger(fromEval) && fromEval > 0) return fromEval
      return 1
    } catch (error) {}
  }

  // ==========================================
  // CONSTRUCCIÓN Y ENVÍO DEL PAYLOAD
  // ==========================================
  const buildPayloads = () => {
    try {
      const payloads = []
      for (const id of selectedIds) {
        const ev = evaluations.find(e => e.id === id)
        const d = evaluationsData[id] || {}
        const isExist = !!d.__existing

        payloads.push({
          name: (d.name ?? ev?.name ?? '').trim(),
          type: ev.type,
          startDate: d.startDate ?? '',
          finishDate: d.finishDate ?? '',
          startHour: toHHMMSS(d.startTime ?? ''),
          finishHour: toHHMMSS(d.finishTime ?? ''),
          oportunities: ResolveOportunities(d, ev),
          idItem: (typeof d.idItem !== 'undefined') ? d.idItem : (ev?.id ?? id),
          idGroup: selectedGroupId,
          idCourse: selectedCourseId,
          isExist,
          idGroupCourse: isExist ? d.idGroupCourse : undefined,
          IdAgenda: isExist ? d.IdAgenda : undefined
        })
      }
      return payloads
    } catch (error) {}
  }

  const CreateAgenda = async () => {
    try {
      setIsWorking(true)
      setErrorMessage(null)

      if (!selectedGroupId) return setErrorMessage('Debes seleccionar un grupo.')
      if (!selectedCourseId) return setErrorMessage('Debes seleccionar un curso.')
      if (!selectedIds.length) return setErrorMessage('Debes seleccionar al menos una evaluación.')
      
      const payloads = buildPayloads()
      
      // 1. Validar Batch
      const response = await BLValidateAgendasBatch(payloads)
      setInputErrors(response.data.fieldErrorsById || {})

      if (response.data.invalid.length > 0) {
        if (selectedIds.length === 1) {
          showMessage({ type: MESSAGE_TYPE.BAD, text: 'No ha sido posible agendar la evaluación solicitada. Verifica los datos.' })
          return
        }
      }

      if (!response.data.valid.length) {
        const msg = selectedIds.length === 1 
            ? 'No ha sido posible agendar la evaluación.' 
            : `No ha sido posible agendar las ${selectedIds.length} evaluaciones.`
        showMessage({ type: MESSAGE_TYPE.BAD, text: msg })
        return
      }

      // 2. Enviar Válidos
      const summary = {
        ok: [],
        fail: [],
        omitted: response.data.invalid.map(x => ({ payload: payloads.find(p => p.idItem === x.idItem), reason: 'Errores de validación' }))
      }

      for (const agenda of response.data.valid) {
        try {
          const responseCreate = await BLCreateAgenda(agenda)
          if (responseCreate?.success) {
            summary.ok.push(agenda)
          } else {
            summary.fail.push({ payload: agenda, message: responseCreate?.message ?? 'Error' })
          }
        } catch (e) {
          summary.fail.push({ payload: agenda, message: e?.message ?? 'Excepción' })
        }
      }
      
      // 3. Feedback Final
      if (selectedIds.length === 1) {
        if (summary.ok.length === 1 && summary.fail.length === 0) {
          showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'La evaluación se ha agendado de manera exitosa' })
          resetAfterCreateKeepCourses()
        } else {
          showMessage({ type: MESSAGE_TYPE.BAD, text: 'No fue posible agendar la evaluación.' })
        }
      } else {
        const successful = summary.ok.map(p => ({ name: p.name || '(Sin nombre)' }))
        const wrongs = summary.fail.map(f => ({ name: f?.payload?.name || '(Sin nombre)' }))
        const omitteds = summary.omitted.map(o => ({ name: o?.payload?.name || '(Sin nombre)' }))

        setMassiveSummary({ successful, wrongs, omitteds })
        setShowMassiveSummaryModal(true)

        if (summary.ok.length > 0) {
          resetAfterCreateKeepCourses()
        } else {
          showMessage({ type: MESSAGE_TYPE.BAD, text: 'No ha sido posible agendar las evaluaciones.' })
        }
      }
      
      const success = summary.fail.length === 0 && summary.ok.length > 0
      return { success, data: summary }

    } catch (error) {
      await LogErrors('CreateAgenda - Create', error)
      return { success: false, message: 'Error al crear la(s) agenda(s).' }
    } finally {
      setIsWorking(false)
    }
  }

  // ==========================================
  // RENDER
  // ==========================================
  return (
      <InstructorBase urlToGo={'/instructor/agenda'} page={WEBPAGES.INSTRUCTOR.AGENDA}>
        <section className='container Instructor__Agendas__Create'>
          <p className='Instructor__Title Instructor__Title--Center Instructor__SpaceUp'>Agendar evaluación</p>

          {courses ? (
            <>
              {/* PASO 1: CURSOS */}
              <p className='Instructor__Text--Center Instructor__SpaceUp'>Selecciona el curso</p>
              <select
                className='Instructor__Agendas__Create__InputList'
                value={selectedCourseId ?? 0}
                onChange={(e) => setSelectedCourseId(null) || handleSelectCourse(parseInt(e.target.value))}
              >
                <option value={0}>Selecciona un curso</option>
                {(courses ?? []).map((course) => (
                  <option key={course.id} value={course.id}>{`${course.name}${course?.level && ` nivel ${course.level}`}`}</option>
                ))}
              </select>
            </>
          ) : (
            <>
              <p className='Instructor__Text--Center Instructor__SpaceUp'>No ha sido posible mostrar el listado de cursos.</p>
              <p className='Instructor__Text--Center'>Actualiza la página o intenta actualizarla más tarde.</p>
            </>
          )}

          {/* PASO 2: GRUPOS */}
          {step >= 2 && (
            <>
              <p className='Instructor__Text--Center Instructor__SpaceUp'>Selecciona el grupo al cual deseas agendarle</p>
              {allRows ? (
                allRows.length === 0 ? (
                  <p className='Instructor__Text--Center Instructor__SpaceUp'>Actualmente no hay grupos disponibles para agendar.</p>
                ) : (
                  <>
                    <Search onChange={handleSearch} extraClassName={'Instructor__Agendas__Create__Groups__Search'} />
                    <section className="Tables__Container Instructor__Agendas__Create__Groups Tables--Shadow Tables--Background">
                      <table className="Tables__Table Instructor__Agendas__Create__Groups__ContainerTable__Table">
                        <thead>
                          <tr className='Tables__Table__Row--Header'>
                            <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Agendas__Create__Groups__ContainerTable__Table__TextColumn'>
                              Nombre del grupo
                            </td>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredRows.map((row) => (
                            <tr
                              key={row.idGroup}
                              className={`${selectedGroupId === row.idGroup ? 'Instructor__Agendas__Create__Groups__ContainerTable__Table__RowSelected' : 'Tables__Table__Row'}`}
                              onClick={() => handleSelectGroup(row)}
                            >
                              <td className="Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Agendas__Create__Groups__ContainerTable__Table__TextColumn">
                                {row.groupName}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </section>
                  </>
                )
              ) : (
                <p className='Instructor__Text--Center'>No ha sido posible mostrar la lista de grupos.</p>
              )}
            </>
          )}

          {/* PASO 3: EVALUACIONES */}
          {step >= 3 && (
            evaluations ? (
              <>
                <p className='Instructor__Text--Center Instructor__SpaceUp'>Selecciona el tipo de evaluación (o tipos) y sus datos</p>

                {evaluations.length === 0 ? (
                  <p className='Instructor__Text--Center Instructor__SpaceUp'>Actualmente no hay evaluaciones disponibles para agendar.</p>
                ) : (
                  <section className="Tables__Container Instructor__Agendas__Create__Evaluations Tables--Shadow Tables--Background">
                    <table className="Tables__Table Instructor__Agendas__Create__Evaluations__ContainerTable__Table">
                      <thead>
                        <tr className='Tables__Table__Row--Header'>
                          <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__InputColumn Instructor__Agendas__Create__Evaluations__ContainerTable__Table__InputColumn'>
                            <input
                              type="checkbox"
                              className="Tables__Table__InputColumn__Input"
                              onChange={toggleSelectAll}
                              checked={selectedIds.length === evaluations.length && evaluations.length > 0}/>
                          </td>
                          <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Agendas__Create__Evaluations__ContainerTable__Table__TextColumn'>Tipo / Evaluación</td>
                          <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Agendas__Create__Evaluations__ContainerTable__Table__TextColumn'>Nombre (opcional)</td>
                          <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__ActionButtonsColumn Instructor__Agendas__Create__Evaluations__ContainerTable__Table__ActionButtonsColumn'>Fecha y hora de inicio</td>
                          <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__ActionButtonsColumn Instructor__Agendas__Create__Evaluations__ContainerTable__Table__ActionButtonsColumn'>Fecha y hora de fin</td>
                          <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__ActionButtonsColumn Instructor__Agendas__Create__Evaluations__ContainerTable__Table__ActionButtonsColumn'>No. de oportunidades</td>
                        </tr>
                      </thead>
                      <tbody>
                        {evaluations.map((row) => {
                          const data = evaluationsData[row.id] ?? {}
                          const active = isSelected(row.id)
                          return (
                            <tr key={row.id} className="Tables__Table__Row">
                              {/* Checkbox */}
                              <td className="Tables__Table__Row__Cell Tables__Table__InputColumn Instructor__Agendas__Create__Evaluations__ContainerTable__Table__InputColumn">
                                <input
                                  type="checkbox"
                                  className="Tables__Table__InputColumn__Input"
                                  checked={active}
                                  onChange={() => toggleSelect(row.id)}/>
                              </td>

                              {/* Nombre Evaluación */}
                              <td className="Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Agendas__Create__Evaluations__ContainerTable__Table__TextColumn">
                                {active && data.__existing && (
                                  <p className='Instructor__Agendas__Create__Evaluations__ContainerTable__Table__TextColumn__Name mb-1'>
                                    <span className="badge text-bg-info">Reprogramando. Esta ya estaba agendada</span>
                                  </p>
                                )}
                                <p className='Instructor__Agendas__Create__Evaluations__ContainerTable__Table__TextColumn__Name mb-0'>{row.name}</p>
                                {/* --- NUEVO: ENLACE PARA COPIAR A LAS DEMÁS --- */}
                                {active && (
                                  selectedIds.length > 1 && (
                                    <div className='mt-2'>
                                      <p className="cursor-pointer mb-0" style={{ color: '#e61b72', fontSize: '10px' }} onClick={() => applyValuesToActiveEvaluations(row.id)} title="Copia las fechas, horas y oportunidades a las demás evaluaciones seleccionadas">
                                        <i className='bi bi-copy'></i> Aplicar fechas, horas y oportunidades a las demás
                                      </p>
                                    </div>
                                  )
                                )}
                              </td>

                              {/* Nombre Custom */}
                              <td className="Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Agendas__Create__Evaluations__ContainerTable__Table__TextColumn">
                                {active && (
                                  <input
                                    type='text'
                                    className='Instructor__Agendas__Create__Evaluations__ContainerTable__Table__Name'
                                    placeholder='Nombre'
                                    value={data.name ?? ''}
                                    onChange={(e) => updateEvalData(row.id, { name: e.target.value })}/>
                                )}
                              </td>

                              {/* Inicio */}
                              <td className="Tables__Table__Row__Cell Tables__Table__ActionButtonsColumn Instructor__Agendas__Create__Evaluations__ContainerTable__Table__ActionButtonsColumn">
                                {active && (
                                  <>
                                    <div className="Instructor__Agendas__Create__Evaluations__ContainerTable__Table__DateAndTime">
                                      <input
                                        type="date"
                                        className={`Instructor__Agendas__Create__Evaluations__ContainerTable__Table__DateAndTime__Date ${inputErrors[row.id]?.startDate ? "Instructor__Agendas__Create__Evaluations__ContainerTable__Table__Input--Error" : ""}`}
                                        value={data.startDate ?? ''}
                                        onChange={(e) => { updateEvalData(row.id, { startDate: e.target.value }); clearFieldError(row.id, 'startDate'); }}
                                      />
                                      <input
                                        type="time"
                                        className={`Instructor__Agendas__Create__Evaluations__ContainerTable__Table__DateAndTime__Time ${inputErrors[row.id]?.startHour ? "Instructor__Agendas__Create__Evaluations__ContainerTable__Table__Input--Error" : ""}`}
                                        value={data.startTime ?? ''}
                                        onChange={(e) => { updateEvalData(row.id, { startTime: e.target.value }); clearFieldError(row.id, 'startHour'); }}
                                      />
                                    </div>

                                    {/* --- NUEVO: ENLACE PARA COPIAR A LAS DEMÁS --- */}
                                    {/* {selectedIds.length > 1 && (
                                      <div className='text-center mt-2'>
                                        <p className="cursor-pointer mb-0 small" style={{ color: '#e61b72' }} onClick={() => applyValuesToActiveEvaluations(row.id)} title="Copia las fechas, horas y oportunidades a las demás evaluaciones seleccionadas">
                                          <i className='bi bi-clipboard'></i> Aplicar a las demás
                                        </p>
                                      </div>
                                    )} */}
                                  </>
                                )}
                              </td>

                              {/* Fin + LINK "Aplicar a las demás" */}
                              <td className="Tables__Table__Row__Cell Tables__Table__ActionButtonsColumn Instructor__Agendas__Create__Evaluations__ContainerTable__Table__ActionButtonsColumn">
                                {active && (
                                  <>
                                    <div className="Instructor__Agendas__Create__Evaluations__ContainerTable__Table__DateAndTime mt-1">
                                      <input
                                        type="date"
                                        className={`Instructor__Agendas__Create__Evaluations__ContainerTable__Table__DateAndTime__Date ${inputErrors[row.id]?.finishDate ? "Instructor__Agendas__Create__Evaluations__ContainerTable__Table__Input--Error" : ""}`}
                                        value={data.finishDate ?? ''}
                                        onChange={(e) => { updateEvalData(row.id, { finishDate: e.target.value }); clearFieldError(row.id, 'finishDate'); }}
                                      />
                                      <input
                                        type="time"
                                        className={`Instructor__Agendas__Create__Evaluations__ContainerTable__Table__DateAndTime__Time ${inputErrors[row.id]?.finishHour ? "Instructor__Agendas__Create__Evaluations__ContainerTable__Table__Input--Error" : ""}`}
                                        value={data.finishTime ?? ''}
                                        onChange={(e) => { updateEvalData(row.id, { finishTime: e.target.value }); clearFieldError(row.id, 'finishHour'); }}
                                      />
                                    </div>

                                    {/* --- NUEVO: ENLACE PARA COPIAR A LAS DEMÁS --- */}
                                    {/* {selectedIds.length > 1 && (
                                      <div className='text-center mt-2'>
                                        <p className="cursor-pointer mb-0 small" style={{ color: '#e61b72' }} onClick={() => applyValuesToActiveEvaluations(row.id)} title="Copia las fechas, horas y oportunidades a las demás evaluaciones seleccionadas">
                                          <i className='bi bi-clipboard'></i> Aplicar a las demás
                                        </p>
                                      </div>
                                    )} */}
                                  </>
                                )}
                              </td>

                              {/* Oportunidades */}
                              <td className="Tables__Table__Row__Cell Tables__Table__ActionButtonsColumn Instructor__Agendas__Create__Evaluations__ContainerTable__Table__ActionButtonsColumn">
                                {active && (
                                  row.opportunities && row.opportunities > 1 ? (
                                    <select
                                      className={`Instructor__Agendas__Create__InputList Instructor__Agendas__Create__Evaluations__ContainerTable__Table__InputList ${inputErrors[row.id]?.opportunities ? "Instructor__Agendas__Create__Evaluations__ContainerTable__Table__Input--Error" : ""}`}
                                      value={evaluationsData[row.id]?.opportunities ?? 0}
                                      onChange={(e) => { handleChangeOportunity(row.id, e.target.value); clearFieldError(row.id, 'opportunities'); }}
                                    >
                                      <option value={0}>Selecciona</option>
                                      {Array.from({ length: row.opportunities }, (_, i) => (
                                        <option key={i+1} value={i+1}>{i+1}</option>
                                      ))}
                                    </select>
                                  ) : (
                                    <p className='Instructor__Text--Center'>1</p>
                                  )
                                )}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </section>
                )}
              </>
            ) : (
              <p className='Instructor__Text--Center Instructor__SpaceUp'>Actualmente no hay evaluaciones disponibles para agendar a este grupo.</p>
            )
          )}

          {/* PASO 4: BOTONES */}
          {step >= 3 && selectedIds.length > 0 && (
            isWorking ? (
              <p className='Btn__Ztudia--Disabled Instructor__Text--Center Instructor__SpaceUp ms-auto me-auto'>Trabajando...</p>
            ) : (
              <div className='Instructor__Agendas__Create__Buttons Instructor__SpaceUp'>
                <button
                  type='button'
                  className='Btn__Ztudia--Primary Instructor__Agendas__Create__Buttons__Button'
                  onClick={CreateAgenda}
                >
                  Agendar evaluación(es)
                </button>
              </div>
            )
          )}

          {errorMessage && errorMessage !== '' && (
            <p className='Instructor__Message--Error'>{errorMessage}</p>
          )}
        </section>

        {isLoading && <Loading />}

        {/* Modal de resumen masivo */}
        {showMassiveSummaryModal && massiveSummary && (
          <Details
            stepButtons={false}
            showCloseModalButtonInContent={true}
            extraClassName={'Modal__Layout--Summary'}
            setShowModal={setShowMassiveSummaryModal}
            setDetailsData={setMassiveSummary}
          >
            <div className='Modal__Layout--Summary__Container'>
              <p className='Modal__Layout--Summary__Title'>Resumen de agendas</p>
              {massiveSummary ? (
                <>
                  {massiveSummary.successful?.length > 0 && (
                    <>
                      <p className='Modal__Layout--Summary__Category'>
                        <i className='bi bi-check-circle'></i> Evaluación(es) agendada(s):
                      </p>
                      <div className='Modal__Layout--Summary__TextContainer'>
                        {massiveSummary.successful.map((s, i) => (
                          <p key={`s-${i}`} className='Modal__Layout--Summary__TextContainer__Text'>{s.name}</p>
                        ))}
                      </div>
                    </>
                  )}
                  {massiveSummary.wrongs?.length > 0 && (
                    <>
                      <p className='Modal__Layout--Summary__Category'>
                        <i className='bi bi-x-circle'></i> No se pudieron agendar:
                      </p>
                      <div className='Modal__Layout--Summary__TextContainer'>
                        {massiveSummary.wrongs.map((w, i) => (
                          <p key={`w-${i}`} className='Modal__Layout--Summary__TextContainer__Text'>{w.name}</p>
                        ))}
                      </div>
                    </>
                  )}
                  {massiveSummary.omitteds?.length > 0 && (
                    <>
                      <p className='Modal__Layout--Summary__Category'>
                        <i className='bi bi-skip-end-btn'></i> Omitidas:
                      </p>
                      <div className='Modal__Layout--Summary__TextContainer'>
                        {massiveSummary.omitteds.map((o, i) => (
                          <p key={`o-${i}`} className='Modal__Layout--Summary__TextContainer__Text'>{o.name}</p>
                        ))}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <p className='Modal__Layout--Summary__Text'>No ha sido posible mostrar el resumen.</p>
              )}
            </div>
          </Details>
        )}
      </InstructorBase>
  )
}

export { Create };
























// /**
//  * CONFIG: comportamiento del autocopiado con oportunidades
//  * - false (default): NO copiar oportunidades; se resolverán por UI/defecto por evaluación
//  * - true: copiar oportunidades y recortarlas (clamp) al máximo permitido por cada evaluación
//  */
// const COPY_OPPORTUNITIES = true

// const Create = () => {
//   const navigate = useNavigate()
//   const { showMessage } = useFloatMessage()

//   // UI & estado general
//   const [errorMessage, setErrorMessage] = useState(null)
//   const [isWorking, setIsWorking] = useState(false)
//   const [step, setStep] = useState(1) // 1= Cursos, 2= Grupos, 3= Evaluaciones
//   const [inputErrors, setInputErrors] = useState({})

//   const [isLoading,setIsLoading] = useState(false)

//   const clearFieldError = (id, field) => {
//     setInputErrors(prev => {
//       if (!prev[id]) return prev
//       const copy = { ...prev }
//       const { [field]: _omit, ...rest } = copy[id]
//       copy[id] = rest
//       if (Object.keys(copy[id]).length === 0) delete copy[id]
//       return copy
//     })
//   }

//   // -------- Modal de resumen masivo --------
//   const [showMassiveSummaryModal, setShowMassiveSummaryModal] = useState(false)
//   const [massiveSummary, setMassiveSummary] = useState(null)
//   // shape: { successful: [{name}], wrongs: [{name}], omitteds: [{name}] }

//   // -------- Grupos --------
//   const [allRows, setAllRows] = useState([])
//   const [searchText, setSearchText] = useState('')
//   const allRowsRef = useRef([])
//   const [selectedGroupId, setSelectedGroupId] = useState(null)
//   useEffect(() => { allRowsRef.current = allRows }, [allRows])

//   const filteredRows = useMemo(() => {
//     if (!searchText) return allRows
//     return allRows.filter((row) =>
//       (row.groupName ?? '').toLowerCase().includes(searchText.toLowerCase())
//     )
//   }, [searchText, allRows])

//   const handleSearch = (value) => setSearchText(value)

//   // -------- Cursos --------
//   const [courses, setCourses] = useState(null)
//   const [selectedCourseId, setSelectedCourseId] = useState(null)

//   // -------- Evaluaciones --------
//   const [evaluations, setEvaluations] = useState(null)
//   const [selectedIds, setSelectedIds] = useState([])
//   const isSelected = (id) => selectedIds.includes(id)

//   // Datos controlados por evaluación (nombre, fechas, horas, oportunidades)
//   const [evaluationsData, setEvaluationsData] = useState({})

//   // ==== Auto-copiado inteligente ====
//   // Si el usuario edita manualmente cualquier campo de los seleccionados, paramos el auto-copy.
//   // Para reactivarlo, el usuario debe deseleccionar TODOS los checks (quedar en 0 seleccionados).
//   const [autoCopyEnabled, setAutoCopyEnabled] = useState(true)
//   const [lastTemplateId, setLastTemplateId] = useState(null) // último ítem activado del cual copiar

//   // -------- Helpers --------
//   const toHHMMSS = (t) => (t ? (t.length === 5 ? `${t}:00` : t) : '')
//   const normalizeCourses = (arr) => (arr ?? []).map(c => ({ id: c.idCourse, name: c.nameCourse, level: c.level }))

//   const clamp = (val, min, max) => {
//     if (typeof val !== 'number' || Number.isNaN(val)) return undefined
//     if (typeof max === 'number') return Math.max(min, Math.min(val, max))
//     return Math.max(min, val)
//   }

//   const normalizeExistingData = (raw, evMaxOpps) => {
//     // normalizador defensivo del payload de GetExistingEvaluationData
//     // intenta mapear a: startDate, finishDate (YYYY-MM-DD),
//     // startTime, finishTime (HH:mm), opportunities (number), name (string)
//     const toTimeHHmm = (s) => {
//       if (!s) return ''
//       // soporta "HH:mm:ss" o "HH:mm"
//       const parts = String(s).split(':')
//       if (parts.length >= 2) return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`
//       return s
//     }

//     const n = (k, fallback) => {
//       const v = Number(raw?.[k])
//       return Number.isFinite(v) ? v : fallback
//     }

//     const s = (k, fallback = '') => {
//       const v = raw?.[k]
//       return (v ?? fallback)
//     }

//     // intentamos distintos nombres típicos
//     const startDate  = s('startDate', s('fechaInicio', s('dateStart', '')))
//     const finishDate = s('finishDate', s('fechaFin', s('dateFinish', '')))

//     const startTime  = toTimeHHmm(s('startHour', s('horaInicio', s('timeStart', ''))))
//     const finishTime = toTimeHHmm(s('finishHour', s('horaFin', s('timeFinish', ''))))

//     const name       = s('name', s('customName', s('titulo', '')))

//     let opportunities = n('opportunities', n('oportunities', n('intentos', undefined)))
//     if (typeof opportunities !== 'undefined') {
//       opportunities = clamp(opportunities, 1, evMaxOpps)
//     }

//     return {
//       startDate,
//       finishDate,
//       startTime,
//       finishTime,
//       opportunities,
//       name,
//       isExist: !!raw?.isExist,
//       idGroupCourse: raw?.idGroupCourse ?? raw?.IdGroupCourse ?? null,
//       IdAgenda: raw?.IdAgenda ?? null
//     }
//   }

//   const updateEvalData = (id, patch, { markManualEdit = true } = {}) => {
//     setEvaluationsData(prev => {
//       const merged = { ...prev[id], ...patch }
//       return { ...prev, [id]: merged }
//     })
//     // Si el usuario cambió un dato → desactivar auto-copy para los siguientes
//     if (markManualEdit) {
//       setAutoCopyEnabled(false)
//     }
//   }

//   const resetAll = () => {
//     try {
//       setErrorMessage(null)
//       setIsWorking(false)

//       setSelectedGroupId(null)
//       setSelectedCourseId(null)

//       setCourses(null)       // reset "completo"
//       setEvaluations(null)

//       setSelectedIds([])
//       setEvaluationsData({})

//       setSearchText('')
//       setStep(1)

//       setAutoCopyEnabled(true)
//       setLastTemplateId(null)
//     } catch (error) {}
//   }

//   // Reset que mantiene cursos cargados y vuelve a paso 1 (para seguir agendando)
//   const resetAfterCreateKeepCourses = () => {
//     try {
//       setErrorMessage(null)
//       setIsWorking(false)

//       // Mantener los cursos ya cargados
//       setSelectedCourseId(null)
//       setSelectedGroupId(null)

//       setAllRows([])         // grupos
//       setEvaluations(null)   // evaluaciones

//       setSelectedIds([])
//       setEvaluationsData({})

//       setSearchText('')
//       setStep(1)

//       // Reactivar auto-copiado
//       setAutoCopyEnabled(true)
//       setLastTemplateId(null)
//     } catch (error) {}
//   }

//   // -------- Cargas iniciales --------
//   const NormalizeGroups = (resp) => {
//     if (!resp?.success) return []
//     return [
//       ...(resp?.data?.visibles   ?? []).map(x => ({ ...x, visible: true })),
//       ...(resp?.data?.noVisibles ?? []).map(x => ({ ...x, visible: false })),
//     ]
//   }

//   const LoadPage = async () => {
//     try {
//       const resp = await BLGetCourses()
//       if (resp?.success) {
//         setCourses(normalizeCourses(resp.data)) // { id, name }
//         setStep(1)
//       } else {
//         setErrorMessage(resp?.message ?? 'No fue posible cargar cursos.')
//       }
//     } catch (error) {
//       await LogErrors('LoadPage - Create', error)
//       setErrorMessage('Ocurrió un error al cargar cursos.')
//     }
//   }

//   useEffect(() => { LoadPage() }, [])

//   // -------- Validación de sesión --------
//   useEffect(() => {
//     async function Validate() {
//       const tmpSession = await ValidateSession()
//       if (tmpSession.success) {
//         if (!tmpSession.data.user.userStatus === 2) {
//           navigate('/confirm')
//         }
//       } else {
//         navigate(`/?logout=${process.env.REACT_APP_LOGOUT_PARAMETER}`)
//       }
//     }
//     Validate()
//   }, [navigate])

//   // -------- Flujo por pasos --------
//   const handleSelectGroup = async (group) => {
//     try {
//       setIsWorking(true)
//       setErrorMessage(null)

//       setSelectedGroupId(group.idGroup)
//       setEvaluations(null)
//       setSelectedIds([])
//       setEvaluationsData({})
//       setAutoCopyEnabled(true)
//       setLastTemplateId(null)

//       // Cargar evaluaciones dependientes del curso
//       const resp = await BLGetEvaluations(selectedCourseId)
//       if (resp?.success) {
//         setEvaluations(resp.data ?? [])
//         setStep(3)
//       } else {
//         setErrorMessage(resp?.message ?? 'No fue posible cargar evaluaciones.')
//       }
//     } catch (e) {
//       await LogErrors('handleSelectGroup - Create', e)
//       setErrorMessage('Ocurrió un error al cargar evaluaciones.')
//     } finally {
//       setIsWorking(false)
//     }
//   }

//   const handleSelectCourse = async (courseId) => {
//     try {
//       setIsWorking(true)
//       setErrorMessage(null)

//       setSelectedCourseId(courseId)
//       setSelectedGroupId(null)
//       setEvaluations(null)
//       setSelectedIds([])
//       setEvaluationsData({})
//       setAutoCopyEnabled(true)
//       setLastTemplateId(null)

//       if (!courseId) return

//       const resp = await GetAllDataGroups()
//       if (resp?.success) {
//         setAllRows(NormalizeGroups(resp))
//         setStep(2)
//       } else {
//         setErrorMessage(resp?.message ?? 'No fue posible cargar grupos.')
//       }
//     } catch (e) {
//       await LogErrors('handleSelectCourse - Create', e)
//       setErrorMessage('Ocurrió un error al cargar grupos.')
//     } finally {
//       setIsWorking(false)
//     }
//   }

//   // ====== Auto-copiado: helpers ======
//   const getCopyableTemplate = () => {
//     if (!autoCopyEnabled || !lastTemplateId) return null
//     const data = evaluationsData[lastTemplateId]
//     if (!data) return null

//     // Por defecto NO copiamos 'opportunities' (ver bandera COPY_OPPORTUNITIES)
//     //const { name, opportunities, ...rest } = data || {}
//     // Extrae y DESCARTA campos meta que NO deben copiarse
//     const {
//       name,
//       opportunities,
//       __existing,
//       IdAgenda,
//       idGroupCourse,
//       idItem,
//       ...rest
//     } = data || {}

//     // Opcionalmente copia oportunidades
//     if (COPY_OPPORTUNITIES) {
//       return { ...rest, opportunities }
//     }
//     return rest
//   }

//   const applyDefaultsIfNeeded = (id) => {
//     // si no hay horas definidas, ponemos por defecto
//     setEvaluationsData(prev => {
//       const base = prev[id] || {}
//       const patch = {}
//       if (!base.startTime) patch.startTime = '00:00'
//       if (!base.finishTime) patch.finishTime = '23:59'
//       return { ...prev, [id]: { ...base, ...patch } }
//     })
//   }

//   // Consulta datos existentes al ACTIVAR una evaluación (si ya fue agendada)
//   const tryPrefillFromExisting = async (ev) => {
//     try {
//       if (!selectedCourseId || !selectedGroupId) return null
      
//       // firma tentativa: ajusta si tu BL requiere otros nombres
//       const resp = await BLGetExistingAgenda(selectedCourseId, selectedGroupId, ev.id)
      
//       // Si no hay success o no trae data usable, salimos
//       if (!resp?.success || !resp?.data) return null

//       // Normalizamos (recorta oportunidades al máximo de la evaluación)
//       const normalized = normalizeExistingData(resp.data, ev.opportunities)
//       return {
//         ...normalized,
//         // Metadatos para el envío posterior:
//         idItem: resp.data.idItem,
//         idGroupCourse: resp.data.idGroupCourse,
//         IdAgenda: resp.data.id
//       }
//       //return normalized
//     } catch (e) {
//       await LogErrors('tryPrefillFromExisting - Create', e)
//       return null
//     }
//   }

//   const toggleSelect = async (id) => {
//     try {
//       setIsLoading(true)
//       const ev = (evaluations || []).find(x => x.id === id)
//       if (!ev) return

//       // ¿Estamos agregando o quitando?
//       const isAdding = !selectedIds.includes(id)
//       if (!isAdding) {
//         // Quitar
//         const updated = selectedIds.filter(i => i !== id)
//         setSelectedIds(updated)
//         if (updated.length === 0) {
//           setAutoCopyEnabled(true)
//           setLastTemplateId(null)
//         }
//         return
//       }

//       // Agregar
//       // 1) Intentar PREFILL desde BD si ya existe agenda para este ítem
//       const existing = await tryPrefillFromExisting(ev)
//       if (existing) {
//         // OJO con oportunidades: si COPY_OPPORTUNITIES=false NO copiamos oportunidades
//         const patch = { ...existing }
//         if (!COPY_OPPORTUNITIES) delete patch.opportunities

//         setEvaluationsData(prev => ({
//           ...prev,
//           [id]: {
//             ...(prev[id] || {}),
//             ...patch,
//             __existing: true,
//             idItem: existing.idItem ?? (prev[id]?.idItem),
//             idGroupCourse: existing.idGroupCourse,
//             IdAgenda: existing.IdAgenda
//           }
//         }))
//         setSelectedIds(prev => [...prev, id])
//         // Este se vuelve el nuevo template (mantener autoCopyEnabled como esté)
//         setLastTemplateId(id)
//         return
//       }

//       // 2) Si no hay datos existentes: intentamos auto-copiado desde último template
//       const template = getCopyableTemplate()
//       if (template) {
//         // Si vamos a copiar oportunidades, recortamos al máximo permitido por esta evaluación
//         const patch = { ...template }

//         // Defensa extra: asegúrate de que NO se cuele nada meta
//         delete patch.__existing
//         delete patch.IdAgenda
//         delete patch.idGroupCourse
//         delete patch.idItem

//         if (typeof patch.opportunities !== 'undefined') {
//           patch.opportunities = clamp(Number(patch.opportunities), 1, ev.opportunities)
//         }

//         setEvaluationsData(prev => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }))
//         setSelectedIds(prev => [...prev, id])
//         setLastTemplateId(id)
//         return
//       }

//       // 3) Sin existing y sin template: defaults mínimos
//       applyDefaultsIfNeeded(id)
//       setSelectedIds(prev => [...prev, id])
//       setLastTemplateId(id)
//     } catch (error) {

//     } finally {
//       setIsLoading(false)
//     }
//   }

//   const toggleSelectAll = async () => {
//     try {
//       setIsLoading(true)
//       if (!evaluations?.length) return
//       const allIds = evaluations.map(r => r.id)
//       const selectingAll = selectedIds.length !== evaluations.length

//       if (!selectingAll) {
//         // Deseleccionar todo → reactivar auto-copy
//         setSelectedIds([])
//         setAutoCopyEnabled(true)
//         setLastTemplateId(null)
//         return
//       }

//       // ===== Seleccionar todo =====
//       setIsWorking(true)
//       setErrorMessage(null)

//       // 1) Intentar buscar datos existentes (BULK si está disponible, si no, uno por uno)
//       // -------------------------------------------------------------------------------
//       // MAPA: idItem -> datos normalizados existentes
//       const existingById = {}

//       // Fallback: si no hay BULK o falló, consultar uno por uno (en paralelo)
//       if (Object.keys(existingById).length === 0) {
//         const tasks = evaluations.map(async (ev) => {
//           const existing = await tryPrefillFromExisting(ev) // ya normaliza y hace clamp
//           if (existing) existingById[ev.id] = existing
//         })
//         await Promise.all(tasks)
//       }

//       // 2) Determinar template de autocopiado (si está habilitado)
//       // ----------------------------------------------------------
//       const template = getCopyableTemplate()

//       // 3) Construir el next evaluationsData aplicando prioridad:
//       //    EXISTENTE > TEMPLATE > DEFAULTS
//       // ----------------------------------------------------------
//       setEvaluationsData(prev => {
//         const next = { ...prev }

//         for (const id of allIds) {
//           const ev = evaluations.find(e => e.id === id)
//           const base = next[id] || {}

//           if (existingById[id]) {
//             // hay datos existentes → aplicar
//             const patch = { ...existingById[id] }
//             if (!COPY_OPPORTUNITIES) delete patch.opportunities

//             next[id] = {
//               ...base,
//               ...patch,
//               __existing: true,
//               idItem: patch.idItem ?? base?.idItem,
//               idGroupCourse: patch.idGroupCourse,
//               IdAgenda: patch.IdAgenda
//             }
//             continue
//           }

//           if (template) {
//             // copiar del template (con clamp de oportunidades si COPY_OPPORTUNITIES = true)
//             const patch = { ...template }

//             // Defensa extra: limpiar meta en bulk también
//             delete patch.__existing
//             delete patch.IdAgenda
//             delete patch.idGroupCourse
//             delete patch.idItem

//             if (typeof patch.opportunities !== 'undefined') {
//               patch.opportunities = clamp(Number(patch.opportunities), 1, ev?.opportunities)
//             }
//             next[id] = { ...base, ...patch }
//             continue
//           }

//           // sin existing y sin template: defaults mínimos de horas
//           const patch = {}
//           if (!base.startTime)  patch.startTime  = '00:00'
//           if (!base.finishTime) patch.finishTime = '23:59'
//           next[id] = { ...base, ...patch }
//         }

//         return next
//       })

//       // 4) Marcar todos como seleccionados y definir template como el último
//       // -------------------------------------------------------------------
//       setSelectedIds(allIds)
//       setLastTemplateId(allIds[allIds.length - 1] || null)

//     } catch (error) {
//       await LogErrors('toggleSelectAll - Create', error)
//     } finally {
//       setIsWorking(false)
//       setIsLoading(false)
//     }
//   }

//   // Mejor: tipar a número + fallback
//   const handleChangeOportunity = useCallback((id, value) => {
//     try {
//       const n = parseInt(value, 10)
//       updateEvalData(id, { opportunities: Number.isInteger(n) && n > 0 ? n : 1 }, { markManualEdit: true })
//     } catch (error) {}
//   }, [])

//   // Prioridad: UI > evaluación > 1
//   const ResolveOportunities = (d, ev) => {
//     try {
//       const fromUI = Number(d?.opportunities)
//       if (Number.isInteger(fromUI) && fromUI > 0) {
//         // Recorta al máximo de la evaluación si existe
//         return clamp(fromUI, 1, ev?.opportunities)
//       }
//       const fromEval = Number(ev?.opportunities)
//       if (Number.isInteger(fromEval) && fromEval > 0) return fromEval
//       return 1
//     } catch (error) {}
//   }

//   // -------- Crear Agendas (payload v2) --------
//   const buildPayloads = () => {
//     try {
//       const payloads = []
//       for (const id of selectedIds) {
//         const ev = evaluations.find(e => e.id === id)
//         const d  = evaluationsData[id] || {}
//         const isExist = !!d.__existing

//         payloads.push({
//           name: (d.name ?? ev?.name ?? '').trim(),
//           type: ev.type,
//           startDate: d.startDate ?? '',
//           finishDate: d.finishDate ?? '',
//           startHour: toHHMMSS(d.startTime ?? ''),
//           finishHour: toHHMMSS(d.finishTime ?? ''),
//           oportunities: ResolveOportunities(d, ev),
//           idItem: (typeof d.idItem !== 'undefined') ? d.idItem : (ev?.id ?? id),
//           idGroup: selectedGroupId,
//           idCourse: selectedCourseId,
//           isExist,
//           idGroupCourse: isExist ? d.idGroupCourse : undefined,
//           IdAgenda: isExist ? d.IdAgenda : undefined
//         })
//       }
//       return payloads
//     } catch (error) {}
//   }

//   const CreateAgenda = async () => {
//     try {
//       setIsWorking(true)
//       setErrorMessage(null)

//       // Validaciones base
//       if (!selectedGroupId) return setErrorMessage('Debes seleccionar un grupo.')
//       if (!selectedCourseId) return setErrorMessage('Debes seleccionar un curso.')
//       if (!selectedIds.length) return setErrorMessage('Debes seleccionar al menos una evaluación.')
      
//       const payloads = buildPayloads()
      
//       // 1) Validar por lote en BL
//       const response = await BLValidateAgendasBatch(payloads)
      
//       // 2) Pintar errores por campo/registro
//       setInputErrors(response.data.fieldErrorsById || {})

//       // 3) Mensaje global si hubo errores
//       if (response.data.invalid.length > 0) {
//         if (selectedIds.length === 1) {
//           showMessage({ type: MESSAGE_TYPE.BAD, text: 'No ha sido posible agendar la evaluación solicitada. Inténtalo nuevamente o inténtalo más tarde.' })
//           return
//         }
//       }

//       // 4) Si no hay válidos, aborta
//       if (!response.data.valid.length) {
//         if (selectedIds.length === 1) {
//           showMessage({ type: MESSAGE_TYPE.BAD, text: 'No ha sido posible agendar la evaluación. Inténtalo nuevamente o inténtalo más tarde' })
//         } else {
//           showMessage({ type: MESSAGE_TYPE.BAD, text: `No ha sido posible agendar las ${selectedIds?.length && `${selectedIds.length} `}evaluaciones. Inténtalo nuevamente o inténtalo más tarde` })
//         }
//         return
//       }

//       // 5) Enviar SOLO los válidos (ya normalizados)
//       const summary = {
//         ok: [],
//         fail: [],
//         omitted: response.data.invalid.map(x => ({ payload: payloads.find(p => p.idItem === x.idItem), reason: 'Errores de validación' }))
//       }

//       for (const agenda of response.data.valid) {
//         try {
//           const responseCreate = await BLCreateAgenda(agenda)
          
//           if (responseCreate?.success) {
//             summary.ok.push(agenda)
//           } else {
//             summary.fail.push({ payload: agenda, message: responseCreate?.message ?? 'Error' })
//           }
//         } catch (e) {
//           summary.fail.push({ payload: agenda, message: e?.message ?? 'Excepción' })
//         }
//       }
      
//       // === Notificaciones y modal según cantidad seleccionada ===
//       if (selectedIds.length === 1) {
//         // Caso una evaluación
//         if (summary.ok.length === 1 && summary.fail.length === 0) {
//           showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'La evaluación se ha agendado de manera exitosa' })
//           // Reset parcial: mantener cursos y volver a paso 1
//           resetAfterCreateKeepCourses()
//         } else {
//           showMessage({ type: MESSAGE_TYPE.BAD, text: 'No fue posible agendar la evaluación. Inténtalo nuevamente o inténtalo más tarde' })
//           // No se resetea: para que el usuario corrija
//         }
//       } else {
//         // Caso varias evaluaciones -> Modal resumen
//         const successful = summary.ok.map(p => ({ name: p.name || '(Sin nombre)' }))
//         const wrongs     = summary.fail.map(f => ({ name: f?.payload?.name || '(Sin nombre)' }))
//         const omitteds   = summary.omitted.map(o => ({ name: o?.payload?.name || '(Sin nombre)' }))

//         setMassiveSummary({ successful, wrongs, omitteds })
//         setShowMassiveSummaryModal(true)

//         // Si hubo al menos una OK → reset parcial para facilitar próximas agendas
//         if (summary.ok.length > 0) {
//           resetAfterCreateKeepCourses()
//         } else {
//           showMessage({ type: MESSAGE_TYPE.BAD, text: 'No ha sido posible agendar las evaluaciones. Inténtalo nuevamente o inténtalo más tarde' })
//         }
//       }
      
//       const success = summary.fail.length === 0 && summary.ok.length > 0
//       return { success, data: summary }
//     } catch (error) {
//       await LogErrors('CreateAgenda - Create', error)
//       return { success: false, message: 'Error al crear la(s) agenda(s).' }
//     } finally {
//       setIsWorking(false)
//     }
//   }


//   //useTawkChat(true) // Activamos el chat en esta página


//   // -------- Render --------
//   return (
//       <InstructorBase urlToGo={'/instructor/agenda'} page={WEBPAGES.INSTRUCTOR.AGENDA}>
//         <section className='container Instructor__Agendas__Create'>
//           <p className='Instructor__Title Instructor__Title--Center Instructor__SpaceUp'>Agendar evaluación</p>

//           {courses ? (
//             <>
//               {/* PASO 1: Cursos */}
//               <p className='Instructor__Text--Center Instructor__SpaceUp'>Selecciona el curso</p>

//               <select
//                 className='Instructor__Agendas__Create__InputList'
//                 value={selectedCourseId ?? 0}
//                 onChange={(e) => setSelectedCourseId(null) || handleSelectCourse(parseInt(e.target.value))}
//               >
//                 <option value={0}>Selecciona un curso</option>
//                 {(courses ?? []).map((course) => (
//                   <option key={course.id} value={course.id}>{`${course.name}${course?.level && ` nivel ${course.level}`}`}</option>
//                 ))}
//               </select>
//             </>
//           ) : (
//             <>
//               <p className='Instructor__Text--Center Instructor__SpaceUp'>No ha sido posible mostrar el listado de cursos.</p>
//               <p className='Instructor__Text--Center'>Actualiza la página o intenta actualizarla más tarde.</p>
//             </>
//           )}

//           {/* PASO 2: Grupos */}
//           {step >= 2 && (
//             <>
//               <p className='Instructor__Text--Center Instructor__SpaceUp'>Selecciona el grupo al cual deseas agendarle</p>

//               {allRows ? (
//                 allRows.length === 0 ? (
//                   <p className='Instructor__Text--Center Instructor__SpaceUp'>
//                     Actualmente no hay grupos disponibles para agendar.
//                   </p>
//                 ) : (
//                   <>
//                     <Search onChange={handleSearch} extraClassName={'Instructor__Agendas__Create__Groups__Search'} />

//                     <section className="Tables__Container Instructor__Agendas__Create__Groups Tables--Shadow Tables--Background">
//                       <table className="Tables__Table Instructor__Agendas__Create__Groups__ContainerTable__Table">
//                         <thead>
//                           <tr className='Tables__Table__Row--Header'>
//                             <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Agendas__Create__Groups__ContainerTable__Table__TextColumn'>
//                               Nombre del grupo
//                             </td>
//                           </tr>
//                         </thead>
//                         <tbody>
//                           {filteredRows.map((row) => (
//                             <tr
//                               key={row.idGroup}
//                               className={`${selectedGroupId === row.idGroup ? 'Instructor__Agendas__Create__Groups__ContainerTable__Table__RowSelected' : 'Tables__Table__Row'}`}
//                               onClick={() => handleSelectGroup(row)}
//                             >
//                               <td className="Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Agendas__Create__Groups__ContainerTable__Table__TextColumn">
//                                 {row.groupName}
//                               </td>
//                             </tr>
//                           ))}
//                         </tbody>
//                       </table>
//                     </section>
//                   </>
//                 )
//               ) : (
//                 <p className='Instructor__Text--Center'>No ha sido posible mostrar la lista de grupos.</p>
//               )}
//             </>
//           )}

//           {/* PASO 3: Evaluaciones */}
//           {step >= 3 && (
//             evaluations ? (
//               <>
//                 <p className='Instructor__Text--Center Instructor__SpaceUp'>Selecciona el tipo de evaluación (o tipos) y sus datos</p>

//                 {evaluations.length === 0 ? (
//                   <p className='Instructor__Text--Center Instructor__SpaceUp'>Actualmente no hay evaluaciones disponibles para agendar.</p>
//                 ) : (
//                   <section className="Tables__Container Instructor__Agendas__Create__Evaluations Tables--Shadow Tables--Background">
//                     <table className="Tables__Table Instructor__Agendas__Create__Evaluations__ContainerTable__Table">
//                       <thead>
//                         <tr className='Tables__Table__Row--Header'>
//                           <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__InputColumn Instructor__Agendas__Create__Evaluations__ContainerTable__Table__InputColumn'>
//                             <input
//                               type="checkbox"
//                               className="Tables__Table__InputColumn__Input"
//                               onChange={toggleSelectAll}
//                               checked={selectedIds.length === evaluations.length && evaluations.length > 0}/>
//                           </td>
//                           <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Agendas__Create__Evaluations__ContainerTable__Table__TextColumn'>Tipo / Evaluación</td>
//                           <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Agendas__Create__Evaluations__ContainerTable__Table__TextColumn'>Nombre (opcional)</td>
//                           <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__ActionButtonsColumn Instructor__Agendas__Create__Evaluations__ContainerTable__Table__ActionButtonsColumn'>Fecha y hora de inicio</td>
//                           <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__ActionButtonsColumn Instructor__Agendas__Create__Evaluations__ContainerTable__Table__ActionButtonsColumn'>Fecha y hora de fin</td>
//                           <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__ActionButtonsColumn Instructor__Agendas__Create__Evaluations__ContainerTable__Table__ActionButtonsColumn'>No. de oportunidades</td>
//                         </tr>
//                       </thead>
//                       <tbody>
//                         {evaluations.map((row) => {
//                           const data = evaluationsData[row.id] ?? {}
//                           return (
//                             <tr key={row.id} className="Tables__Table__Row">
//                               {/* Checkbox de selección con prefill/autocopiado */}
//                               <td className="Tables__Table__Row__Cell Tables__Table__InputColumn Instructor__Agendas__Create__Evaluations__ContainerTable__Table__InputColumn">
//                                 <input
//                                   type="checkbox"
//                                   className="Tables__Table__InputColumn__Input"
//                                   checked={isSelected(row.id)}
//                                   onChange={() => toggleSelect(row.id)}/>
//                               </td>

//                               {/* Nombre evaluación base */}
//                               <td className="Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Agendas__Create__Evaluations__ContainerTable__Table__TextColumn">
//                                 {(() => {
//                                   const d = evaluationsData[row.id] || {}
//                                   const isExisting = !!d.__existing
                                  
//                                   return (
//                                     <>
//                                       {isSelected(row.id) && isExisting && (
//                                         <p className='Instructor__Agendas__Create__Evaluations__ContainerTable__Table__TextColumn__Name mb-1'>
//                                           <span className="badge text-bg-info">Reprogramando. Esta ya estaba agendada</span>
//                                         </p>
//                                       )}
//                                       <p className='Instructor__Agendas__Create__Evaluations__ContainerTable__Table__TextColumn__Name mb-0'>{row.name}</p>
//                                     </>
//                                   )
//                                 })()}
//                               </td>

//                               {/* Nombre (opcional) */}
//                               <td className="Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Agendas__Create__Evaluations__ContainerTable__Table__TextColumn">
//                                 {isSelected(row.id) && (
//                                   <input
//                                     type='text'
//                                     className='Instructor__Agendas__Create__Evaluations__ContainerTable__Table__Name'
//                                     placeholder='Nombre'
//                                     value={data.name ?? ''}
//                                     onChange={(e) => updateEvalData(row.id, { name: e.target.value }, { markManualEdit: true })}/>
//                                 )}
//                               </td>

//                               {/* Inicio */}
//                               <td className="Tables__Table__Row__Cell Tables__Table__ActionButtonsColumn Instructor__Agendas__Create__Evaluations__ContainerTable__Table__ActionButtonsColumn">
//                                 {isSelected(row.id) && (
//                                   <div className="Instructor__Agendas__Create__Evaluations__ContainerTable__Table__DateAndTime">
//                                     <input
//                                       type="date"
//                                       className={
//                                         "Instructor__Agendas__Create__Evaluations__ContainerTable__Table__DateAndTime__Date" +
//                                         (inputErrors[row.id]?.startDate ? " Instructor__Agendas__Create__Evaluations__ContainerTable__Table__Input--Error" : "")
//                                       }
//                                       value={data.startDate ?? ''}
//                                       onChange={(e) => {
//                                         updateEvalData(row.id, { startDate: e.target.value }, { markManualEdit: true })
//                                         clearFieldError(row.id, 'startDate')
//                                       }}
//                                     />
//                                     <input
//                                       type="time"
//                                       className={
//                                         "Instructor__Agendas__Create__Evaluations__ContainerTable__Table__DateAndTime__Time" +
//                                         (inputErrors[row.id]?.startHour ? " Instructor__Agendas__Create__Evaluations__ContainerTable__Table__Input--Error" : "")
//                                       }
//                                       value={data.startTime ?? ''}
//                                       onChange={(e) => {
//                                         updateEvalData(row.id, { startTime: e.target.value }, { markManualEdit: true })
//                                         clearFieldError(row.id, 'startHour')
//                                       }}
//                                     />
//                                   </div>
//                                 )}
//                               </td>

//                               {/* Fin */}
//                               <td className="Tables__Table__Row__Cell Tables__Table__ActionButtonsColumn Instructor__Agendas__Create__Evaluations__ContainerTable__Table__ActionButtonsColumn">
//                                 {isSelected(row.id) && (
//                                   <div className="Instructor__Agendas__Create__Evaluations__ContainerTable__Table__DateAndTime">
//                                     <input
//                                       type="date"
//                                       className={
//                                         "Instructor__Agendas__Create__Evaluations__ContainerTable__Table__DateAndTime__Date" +
//                                         (inputErrors[row.id]?.finishDate ? " Instructor__Agendas__Create__Evaluations__ContainerTable__Table__Input--Error" : "")
//                                       }
//                                       value={data.finishDate ?? ''}
//                                       onChange={(e) => {
//                                         updateEvalData(row.id, { finishDate: e.target.value }, { markManualEdit: true })
//                                         clearFieldError(row.id, 'finishDate')
//                                       }}
//                                     />
//                                     <input
//                                       type="time"
//                                       className={
//                                         "Instructor__Agendas__Create__Evaluations__ContainerTable__Table__DateAndTime__Time" +
//                                         (inputErrors[row.id]?.finishHour ? " Instructor__Agendas__Create__Evaluations__ContainerTable__Table__Input--Error" : "")
//                                       }
//                                       value={data.finishTime ?? ''}
//                                       onChange={(e) => {
//                                         updateEvalData(row.id, { finishTime: e.target.value }, { markManualEdit: true })
//                                         clearFieldError(row.id, 'finishHour')
//                                       }}
//                                     />
//                                   </div>
//                                 )}
//                               </td>

//                               {/* Oportunidades */}
//                               <td className="Tables__Table__Row__Cell Tables__Table__ActionButtonsColumn Instructor__Agendas__Create__Evaluations__ContainerTable__Table__ActionButtonsColumn">
//                                 {isSelected(row.id) && (
//                                   row.opportunities && row.opportunities > 1 ? (
//                                     <select
//                                       className={
//                                         'Instructor__Agendas__Create__InputList Instructor__Agendas__Create__Evaluations__ContainerTable__Table__InputList' +
//                                         (inputErrors[row.id]?.opportunities ? " Instructor__Agendas__Create__Evaluations__ContainerTable__Table__Input--Error" : "")
//                                       }
//                                       value={evaluationsData[row.id]?.opportunities ?? 0}
//                                       onChange={(e) => {
//                                         handleChangeOportunity(row.id, e.target.value)
//                                         clearFieldError(row.id, 'opportunities')
//                                       }}
//                                     >
//                                       <option value={0}>Selecciona una opción</option>
//                                       {Array.from({ length: row.opportunities }, (_, i) => (
//                                         <option key={i+1} value={i+1}>{i+1}</option>
//                                       ))}
//                                     </select>
//                                   ) : (
//                                     <p className='Instructor__Text--Center'>1</p>
//                                   )
//                                 )}
//                               </td>
//                             </tr>
//                           )
//                         })}
//                       </tbody>
//                     </table>
//                   </section>
//                 )}
//               </>
//             ) : (
//               <p className='Instructor__Text--Center Instructor__SpaceUp'>Actualmente no hay evaluaciones disponibles para agendar a este grupo.</p>
//             )
//           )}

//           {/* PASO 4: Botones */}
//           {step >= 3 && selectedIds.length > 0 && (
//             isWorking ? (
//               <p className='Btn__Ztudia--Disabled Instructor__Text--Center Instructor__SpaceUp ms-auto me-auto'>Trabajando...</p>
//             ) : (
//               <div className='Instructor__Agendas__Create__Buttons Instructor__SpaceUp'>
//                 <button
//                   type='button'
//                   className='Btn__Ztudia--Primary Instructor__Agendas__Create__Buttons__Button'
//                   onClick={CreateAgenda}
//                 >
//                   Agendar evaluación(es)
//                 </button>
//               </div>
//             )
//           )}

//           {errorMessage && errorMessage !== '' && (
//             <p className='Instructor__Message--Error'>{errorMessage}</p>
//           )}
//         </section>

//         {isLoading && (
//           <Loading />
//         )}

//         {/* Modal de resumen masivo */}
//         {showMassiveSummaryModal && massiveSummary && (
//           <Details
//             stepButtons={false}
//             showCloseModalButtonInContent={true}
//             extraClassName={'Modal__Layout--Summary'}
//             setShowModal={setShowMassiveSummaryModal}
//             setDetailsData={setMassiveSummary}
//           >
//             <div className='Modal__Layout--Summary__Container'>
//               <p className='Modal__Layout--Summary__Title'>Resumen de agendas</p>

//               {massiveSummary ? (
//                 <>
//                   {massiveSummary.successful?.length > 0 && (
//                     <>
//                       <p className='Modal__Layout--Summary__Category'>
//                         <i className='bi bi-check-circle'></i> Evaluación(es) agendada(s):
//                       </p>
//                       <div className='Modal__Layout--Summary__TextContainer'>
//                         {massiveSummary.successful.map((s, i) => (
//                           <p key={`s-${i}`} className='Modal__Layout--Summary__TextContainer__Text'>{s.name}</p>
//                         ))}
//                       </div>
//                     </>
//                   )}

//                   {massiveSummary.wrongs?.length > 0 && (
//                     <>
//                       <p className='Modal__Layout--Summary__Category'>
//                         <i className='bi bi-x-circle'></i> No se pudieron agendar:
//                       </p>
//                       <div className='Modal__Layout--Summary__TextContainer'>
//                         {massiveSummary.wrongs.map((w, i) => (
//                           <p key={`w-${i}`} className='Modal__Layout--Summary__TextContainer__Text'>{w.name}</p>
//                         ))}
//                       </div>
//                     </>
//                   )}

//                   {massiveSummary.omitteds?.length > 0 && (
//                     <>
//                       <p className='Modal__Layout--Summary__Category'>
//                         <i className='bi bi-skip-end-btn'></i> Omitidas:
//                       </p>
//                       <div className='Modal__Layout--Summary__TextContainer'>
//                         {massiveSummary.omitteds.map((o, i) => (
//                           <p key={`o-${i}`} className='Modal__Layout--Summary__TextContainer__Text'>{o.name}</p>
//                         ))}
//                       </div>
//                     </>
//                   )}
//                 </>
//               ) : (
//                 <p className='Modal__Layout--Summary__Text'>No ha sido posible mostrar el resumen.</p>
//               )}
//             </div>
//           </Details>
//         )}
//       </InstructorBase>
//   )
// }

// export { Create };